import { createContext, useContext, useEffect, useId, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { IconButton } from './controls';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

let scrollLocks = 0;

/**
 * Shared overlay behaviour: portal to <body>, lock page scroll, trap Tab
 * inside the panel, close on Escape, and hand focus back to whatever
 * opened it.
 */
function useOverlay(open: boolean, onClose: () => void) {
  const panel = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    if (scrollLocks++ === 0) document.body.style.overflow = 'hidden';

    const node = panel.current;
    const first = node?.querySelector<HTMLElement>('[autofocus], [data-autofocus]') ?? node;
    first?.focus({ preventScroll: true });

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !node) return;
      const items = [...node.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const firstItem = items[0]!;
      const lastItem = items[items.length - 1]!;
      if (e.shiftKey && (document.activeElement === firstItem || document.activeElement === node)) {
        e.preventDefault();
        lastItem.focus();
      } else if (!e.shiftKey && document.activeElement === lastItem) {
        e.preventDefault();
        firstItem.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (--scrollLocks === 0) document.body.style.overflow = '';
      opener?.focus?.({ preventScroll: true });
    };
  }, [open]);

  return panel;
}

interface OverlayProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Visually hide the title (still names the dialog). */
  hideTitle?: boolean;
  children: ReactNode;
  footer?: ReactNode;
}

/**
 * Centered dialog. Reserve it for decisions that need protected focus
 * (destructive confirmations, sign-in); everything else is a sheet or inline.
 */
export function Modal({ open, onClose, title, hideTitle, children, footer }: OverlayProps) {
  const { t } = useTranslation();
  const panel = useOverlay(open, onClose);
  const titleId = useId();
  if (!open) return null;
  return createPortal(
    <div className="overlay overlay-center" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={panel} className="modal" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <header className="overlay-header">
          <h2 id={titleId} className={hideTitle ? 'visually-hidden' : undefined}>
            {title}
          </h2>
          <IconButton icon={X} label={t('common.close')} onClick={onClose} className="overlay-close" />
        </header>
        <div className="overlay-body">{children}</div>
        {footer && <footer className="overlay-footer">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}

/**
 * Height the on-screen keyboard covers, from the visual viewport. On
 * WebViews that do not resize for the keyboard (iOS WKWebView without the
 * Keyboard plugin) a bottom sheet would otherwise sit behind it; where the
 * WebView already resizes (Android adjustResize) this stays 0.
 */
function useKeyboardInset(active: boolean): number {
  const [inset, setInset] = useState(0);
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!active || !vv) return;
    const update = () => setInset(Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop)));
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, [active]);
  return inset;
}

/**
 * Bottom sheet on phones (drag the handle or the header down to dismiss),
 * a centered panel on wide screens. The default container for quick tasks.
 * It rides above the on-screen keyboard.
 */
export function BottomSheet({ open, onClose, title, hideTitle, children, footer }: OverlayProps) {
  const { t } = useTranslation();
  const panel = useOverlay(open, onClose);
  const titleId = useId();
  const [drag, setDrag] = useState(0);
  const start = useRef<number | null>(null);
  const keyboard = useKeyboardInset(open);

  if (!open) return null;

  const onPointerDown = (e: ReactPointerEvent) => {
    if ((e.target as HTMLElement).closest('button, a, input, select, textarea')) return;
    start.current = e.clientY;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: ReactPointerEvent) => {
    if (start.current !== null) setDrag(Math.max(0, e.clientY - start.current));
  };
  const onPointerUp = () => {
    if (start.current === null) return;
    start.current = null;
    if (drag > 96) onClose();
    setDrag(0);
  };

  return createPortal(
    <div
      className="overlay overlay-sheet"
      style={keyboard ? { paddingBottom: keyboard } : undefined}
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        ref={panel}
        className={drag ? 'sheet sheet-dragging' : 'sheet'}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        style={drag ? { transform: `translateY(${drag}px)` } : undefined}
      >
        <div
          className="sheet-grab"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <span className="sheet-handle" aria-hidden />
          <header className="overlay-header">
            <h2 id={titleId} className={hideTitle ? 'visually-hidden' : undefined}>
              {title}
            </h2>
            <IconButton icon={X} label={t('common.close')} onClick={onClose} className="overlay-close" />
          </header>
        </div>
        <div className="overlay-body">{children}</div>
        {footer && <footer className="overlay-footer">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}

/* ---------------------------------------------------------------- toast */

interface ToastItem {
  id: number;
  message: string;
  action?: { label: string; onClick: () => void };
}

const ToastCtx = createContext<{ show: (t: Omit<ToastItem, 'id'>) => void } | null>(null);

/**
 * One short confirmation at a time, above the tab bar, with an optional
 * action (Undo). Announced politely; stays 5 s, longer while focused.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastItem | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const nextId = useRef(1);

  const dismiss = () => {
    clearTimeout(timer.current);
    setToast(null);
  };
  const arm = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(null), 5000);
  };
  const value = useMemo(
    () => ({
      show(t: Omit<ToastItem, 'id'>) {
        setToast({ ...t, id: nextId.current++ });
        arm();
      },
    }),
    [],
  );
  useEffect(() => () => clearTimeout(timer.current), []);

  return (
    <ToastCtx.Provider value={value}>
      {children}
      <div className="toast-region" role="status" aria-live="polite">
        {toast && (
          <div key={toast.id} className="toast" onFocus={() => clearTimeout(timer.current)} onBlur={arm}>
            <span className="toast-message">{toast.message}</span>
            {toast.action && (
              <button
                type="button"
                className="toast-action"
                onClick={() => {
                  toast.action!.onClick();
                  dismiss();
                }}
              >
                {toast.action.label}
              </button>
            )}
          </div>
        )}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast outside ToastProvider');
  return ctx;
}
